// 幕間の物語パート (ADV) のデータモデルとエンジン。 純関数・DOM非依存。
// 正本: docs/superpowers/specs/2026-06-12-adv-scene-design.md

export type AdvCue =
  | 'lamp_pulse' | 'silhouette_in' | 'silhouette_out' | 'voice_warp'
  | 'flashback' | 'slide' | 'lamp_focus' | 'world_out' | 'text_cut' | 'blackout' | `sfx:${string}` | `amb:${string}`

// テキスト行ごとの送り方 (2026-10-27 花嫁の会話場面で追加・どの場面でも使える)。
//   pace: 文字送りの速さ (無し=ADV_TYPEWRITER)。 pre: 行に入ってから文字を出し始めるまでの待ち (ms・送り入力も受けない)。
//   auto: 出し切ってから、 送り入力なしで次へ進むまでの ms。
export type AdvPace = keyof typeof ADV_PACES
export type AdvTextTiming = { pace?: AdvPace; pre?: number; auto?: number }

// visibleLines が返す「テキストを持つ行」だけの union。
export type AdvTextLine = AdvTextTiming & (
  | { kind: 'voice'; text: string }                       // 神の声
  // LUNAIRE。 speech 無し=従来どおり (絵の場面は括弧なし・文字だけの場面は（）)。
  //   'spoken'=口に出す「」・'inner'=心の中（）。 どちらの場面でも同じ括弧になる。
  | { kind: 'lunaire'; text: string; speech?: 'spoken' | 'inner' }
  | { kind: 'other'; text: string; silhouette?: string }  // 相手の最期
  | { kind: 'narration'; text: string }                   // 地の文
  | { kind: 'boss'; text: string; name?: string })        // ボスセリフ

// 背景と別に立つ人物の絵。 src=null で下げる。 位置と大きさは画面に対する割合
//   (x=中心の横位置 既定0.5・bottom=足元の高さ 既定1=画面の下端・height=絵の高さ 既定1)。
//   fadeMs=前の人物絵との入れ替え時間 (既定200・0=間を置かず差し替え)。 位置・大きさの変化は描画側で短く寄せる。
export type AdvFigure = { src: string | null; x?: number; bottom?: number; height?: number; fadeMs?: number }

// 気持ちの見せ方 (2026-10-27・使い分けの決まり＝docs/research/2026-10-27-adv-emotion-staging.md)。 絵の場面だけで効く。
//   stage 行は即時送り。 行を通り過ぎた時刻から効き始める (スキップで飛ばした分は、 残る物だけ最後の形で出す)。
//   揺れ・震えは音と一緒に出す (同じ所に cue 'sfx:…' を置く)。 長さは ms、 強さは power (px)。
export type AdvStage =
  | { effect: 'shake'; power?: number; ms?: number }    // 画面の揺れ (既定 10px・350ms。 地鳴りなど弱く長く＝power 3, ms 1200)
  | { effect: 'tremble'; power?: number; ms?: number }  // 人物絵だけの細かい震え (既定 3px・800ms)
  | { effect: 'sink'; power?: number; ms?: number }     // 人物絵が沈む (既定 24px・450ms)。 次の figure 行まで沈んだまま
  | { effect: 'hop'; power?: number; ms?: number }      // 人物絵が小さく跳ねて戻る (既定 14px・260ms)
  | { effect: 'zoom'; scale: number; x?: number; y?: number; ms?: number } // 寄り/引き (scale 1=元。 中心 x,y は画面に対する割合・既定 .5,.42)。 ms 小=急 (既定120)・大=ゆっくり。 次の zoom まで残る
  | { effect: 'flash'; ms?: number }                    // 白い一瞬 (既定 140ms)
  | { effect: 'insert'; src: string | null; fadeMs?: number; fig?: string; push?: AdvPush } // 割り込みの寄りの1枚 (背景と人物の上・文字窓の下)。 src=null で下げる。 fadeMs 既定0
  //   前の割り込みがあれば fadeMs で重ねて入れ替える (src=null+fadeMs はゆっくり消える)。
  //   fig＝同じ絵の人物だけの切り抜き (lean で動かす)・push＝出てからゆっくり寄る (等速)。 2026-10-29 プロローグの窓
  | { effect: 'lean'; scale?: number; x?: number; y?: number; ms?: number } // 割り込みの切り抜き (無ければ割り込み全体) が少し前へ出て戻る。 既定 1.04 倍・往復 1000ms・中心は足元 (x .5, y .9)
  // 倒した相手が光になって消える (2026-10-29 作者「会話の最後に消えていって、光になって」・全部屋共通)。
  //   絵の中の相手の所 (中心 x,y・半径 rx,ry は絵に対する割合) から先にほどけ、 部屋はあとから黒へ落ちる。
  //   ms 既定 4200。 tint=粒の色 (silver=淡い銀・warm=暖かい灯)・rise=粒が上へ流れる。 一度消えたら場面の終わりまで黒のまま
  | { effect: 'dissolve'; x?: number; y?: number; rx?: number; ry?: number; ms?: number; tint?: 'silver' | 'blue'; rise?: boolean }
  // 常夜燈の青白い光が、 出どころの分からないまま絵の上を漂う (2026-10-30 作者「城に青白い常夜燈の光が漂っている感じに」)。
  //   ms かけて現れ、 次の picture 行まで残る。 x,y,rx,ry＝漂う範囲 (絵に対する割合・既定は丘の城のまわり)
  | { effect: 'lampGlow'; ms?: number; x?: number; y?: number; rx?: number; ry?: number }
  // 1枚の絵が風に吹かれて、 ちぎれながら横へ流れて消える (out)・風に乗って入ってくる (in)。 src＝その絵。
  //   out＝picture 行で次の絵へ fadeMs 0 で替えた直後に置く・in＝前の絵の上に入れ、 終わったら picture 行で src へ替える。
  //   2026-10-30 作者「垂れ幕が降りてくるような絵になっている 風と一緒に消えるような演出にしてほしい」(プロローグの外套)
  | { effect: 'wind'; src: string; dir?: 'in' | 'out'; ms?: number }
  // 切り貼りの紙芝居の1枚 (白い縁の紙人形を紙の地に貼る・1枚ごとにぱっと替わる)。 次の picture 行まで画面ぜんたいを覆う。
  //   page ごとの動きは src/adv_collage_view.ts。 2026-10-30 作者「絵を切り貼りしたようなデザインで、この城がどういう状況なのか伝える」
  | { effect: 'collage'; page: AdvCollagePage }
  // テレビの砂嵐。 ms かけてザーッと濃くなり、 次の picture 行まで画面を覆う (文字窓の下)。 この後に暗転の cue を置く。
  //   2026-10-30 作者「テレビの砂嵐みたいにザーってなって一瞬暗転して、月だけが映る絵に」(プロローグで月喰いの王が消える所)
  | { effect: 'static'; ms?: number }

export type AdvCollagePage = 'castle' | 'cloak' | 'taken' | 'princess' | 'sleep' | 'wake' | 'refuse' | 'call' | 'callAgain' | 'ages' | 'peel'
export type AdvPush = { scale: number; ms: number; x?: number; y?: number }
export type AdvLine =
  | AdvTextLine
  | { kind: 'beat'; ms: number; pictureOnly?: boolean }   // 沈黙の間。 pictureOnly＝文字窓を消して絵だけ見せ、 送り入力で早送りできる (2026-10-29 プロローグ)
  | { kind: 'picture'; src: string; fadeMs?: number }    // 即時送り・背景絵 (fadeMs 既定500)
  | ({ kind: 'figure' } & AdvFigure)                      // 即時送り・人物絵
  | ({ kind: 'stage' } & AdvStage)                        // 即時送り・気持ちの見せ方
  | { kind: 'cue'; effect: AdvCue }                       // 演出指示

export type AdvChoiceOption = { text: string; awaken: number; resultLines?: AdvLine[] }
export type AdvChoice = {
  prompt?: string
  options: AdvChoiceOption[]
  timeoutMs?: number
  defaultIndex?: number
}

export type AdvScene = {
  id: string
  lines: AdvLine[]
  choice?: AdvChoice
}

// game.ts が毎tick渡す、解釈済みの入力。
export type AdvInput = {
  advance: boolean       // 次の行へ (テキスト行で待っている時に有効)
  skip: boolean          // 選択の手前までシーンを早送り
  choiceMove: -1 | 0 | 1 // 選択カーソル移動 (この tick の差分)
  confirm: boolean       // 選択の確定
}

export type AdvRuntime = {
  sceneId: string
  cursor: number          // 現在「アクティブ」な line index。 activeLines()[cursor] を処理中
  lineElapsedMs: number   // 現行に入ってからの経過 (beat)
  revealedChars: number   // 現在のテキスト行で表示済みの文字数 (表示用文字列ベース)
  typewriterElapsedMs: number // 次の1文字までに溜まった時間
  choosing: boolean       // 選択UI表示中
  choiceIndex: number
  choiceElapsedMs: number
  choiceResolved: boolean       // 選択確定済み (以後 scene.choice には戻らない)
  resultLines: AdvLine[] | null // 確定した選択肢の resultLines (再生中はこちらを cursor で読む)
  done: boolean           // 完走 (game.ts が次フェーズへ抜けてよい)
  clockMs?: number        // 場面に入ってからの経過 (stage の効き始めを測る)
  stageLog?: string[]     // 通り過ぎた stage 行と時刻 `<r|m><index>@<clockMs>` (スキップで飛ばした行は載らない)
  exitMs?: number         // 戦いへ抜ける途中の経過 (曲を消して暗転してから戦いへ・ADV_BATTLE_EXIT_MS)
  entryWaitMs?: number    // 場面の頭で絵を待って黒のまま止めている経過 (src/battle_asset_gate.ts holdAdvEntry)
  entryReleased?: boolean // 頭の待ちを抜けた (以後は待たない)
}

// 戦う前の場面から戦いへ移る間 (2026-10-28 作者「戦闘への移行時にBGMはフェードアウトさせて、それから戦闘画面に移行してほしい」)。
//   この間に曲と環境音を消し、 画面を黒へ落としてから戦いを始める。
export const ADV_BATTLE_EXIT_MS = 1200

// tick の戻り: 更新後ランタイム + この tick で確定した目覚め度 + 発火した cue (描画/FX用)。
export type AdvTick = { rt: AdvRuntime; awakenDelta: number; firedCue: AdvCue | null }

// ADV 本文の文字送り。実機で読ませる速さを調整するときはここだけを変える。
export const ADV_TYPEWRITER = {
  charMs: 60,
  fullStopExtraMs: 600,
  commaExtraMs: 210,
} as const

// 行ごとの pace で差し替える文字送り。 rush=取り乱して畳みかける・quick=軽口・slow=上品な口上 (10-27 剣舞)。
export const ADV_PACES = {
  rush: { charMs: 32, fullStopExtraMs: 220, commaExtraMs: 70 },
  slow: { charMs: 78, fullStopExtraMs: ADV_TYPEWRITER.fullStopExtraMs, commaExtraMs: 280 },
  quick: { charMs: 38, fullStopExtraMs: ADV_TYPEWRITER.fullStopExtraMs, commaExtraMs: ADV_TYPEWRITER.commaExtraMs },
} as const

// 選択肢は「本文の続き」として即座には出さず、 一拍おいてから、 ゆっくり現れる
//   (2026-07-25 第4段-b ユーザー裁定)。 連打・長押しで本文を送っている人が、
//   選択肢が出たことに気づかないまま片方を確定してしまうため (友達の実プレイ報告)。
//   この2つを足した時間 (= CHOICE_INPUT_GRACE_MS) の間は、 表示も入力も完全に止める:
//   カーソル移動・確定・クリックのいずれも受け付けない。
//   ⚠️ 「一度ボタンを離さないと決定できない」方式は 2026-07-25 に不採用 (ユーザー裁定)。
//     連打そのものは許し、 出るまでの間だけで気づかせる。
//   ⚠️ phaseElapsedSec の 0.6 秒ガードは「フェーズ開始から」であり、 選択肢が出た時点では
//     既に過ぎている。 これは「選択肢が出てからの経過」を見る、 別の下限。
export const CHOICE_REVEAL_DELAY_MS = 600  // 本文が終わってから、 何も出ない間
export const CHOICE_REVEAL_FADE_MS = 400   // 選択肢パネルがじわっと現れる時間

// 🔴 2026-07-26 追加 (友達FB再現の結果)。 これが無いと第4段-b の対策は「読める時間」を作らない。
//   実測 (tests/adv_choice_skip_probe.test.ts): 連打していると pad ✕ / 帯の上のクリックとも
//   **1017ms で確定** した。 パネルが完全に見えるのも 1000ms・入力が開くのも 1000ms で同時刻だったため、
//   **完全に読める状態は実質1フレーム (約17ms) しか無い**まま既定選択で確定していた。
//   → 「完全に見える」と「入力を受け付ける」を分け、 その間に読む時間を挟む。
//   裏取り: Ren'Py 公式 (preferences.skip_after_choices の既定 False = 選択肢は早送りを止める) /
//     Game Accessibility Guidelines「cool-down period (post acceptance delay) of 0.5 seconds
//     between inputs」「Avoid repeated inputs (button-mashing)」/ Dragon Age Origins 以降が
//     採った「送りと選択の間に間を置く」定石 (Mass Effect が長年これを欠いて批判された)。
//   ⚠️ 連打を許す方針のままなので、 これは「完全な防止」ではなく「気づく時間を作る」対策。
//     完全に防ぐには送りと選択でボタンを分けるしかないが、 それは 2026-07-24「決定は✕へ統一」を壊す。
//   ⬜ 実機体感でここだけ調整できる (伸ばす=気づきやすいがもたつく / 縮める=逆)。
export const CHOICE_READ_MS = 800

export const CHOICE_INPUT_GRACE_MS = CHOICE_REVEAL_DELAY_MS + CHOICE_REVEAL_FADE_MS + CHOICE_READ_MS

// パネルが完全に見えきる時刻 (= フェード完了)。 入力が開く時刻 (CHOICE_INPUT_GRACE_MS) とは別物。
//   ⚠️ この2つを再び同値にすると上記の穴が戻る。
export const CHOICE_FULLY_VISIBLE_MS = CHOICE_REVEAL_DELAY_MS + CHOICE_REVEAL_FADE_MS

// 選択肢パネルの不透明度 (0..1)。 描画と当たり判定が同じ式を見るための唯一の出所。
export function choiceRevealAlpha(rt: AdvRuntime): number {
  const t = rt.choiceElapsedMs - CHOICE_REVEAL_DELAY_MS
  if (t <= 0) return 0
  return Math.min(1, t / CHOICE_REVEAL_FADE_MS)
}

// 選択肢への入力 (カーソル移動・確定・クリック) を受け付けてよいか。
//   ⚠️ 完全に見えた後、 さらに CHOICE_READ_MS だけ閉じたまま = 読む時間。
//   描画と入力が同じ出所を見るように、 境界の判定はここだけに置く。
export function isChoiceInputOpen(rt: AdvRuntime): boolean {
  return rt.choiceElapsedMs >= CHOICE_INPUT_GRACE_MS
}

// 「選択肢が現れ始めた」1 tick を検出する (C案: 音を1回だけ鳴らすため)。
//   フェードが始まる瞬間 = 画面に何かが出た瞬間に合わせる (入力が開く時刻ではない。
//   連打している人に「今なにか出た」を先に知らせるのが目的なので、 早い側で鳴らす)。
export function choiceRevealJustStarted(beforeMs: number, afterMs: number): boolean {
  return beforeMs < CHOICE_REVEAL_DELAY_MS && afterMs >= CHOICE_REVEAL_DELAY_MS
}

export function startScene(scene: AdvScene): AdvRuntime {
  const rt: AdvRuntime = {
    sceneId: scene.id, cursor: 0, lineElapsedMs: 0,
    revealedChars: 0, typewriterElapsedMs: 0,
    // 先頭にカーソルが乗った状態で始める (他ゲーム標準・2026-07-25 第4段-b で復帰)。
    //   一時期「未選択 (-1) から方向キーを押さないと決定できない」方式にしていたが、
    //   縦に並んだ選択肢で1回余分に押させるのは他ゲームに無い作法でありユーザー裁定で撤回。
    //   誤爆防止は CHOICE_REVEAL_DELAY_MS + CHOICE_REVEAL_FADE_MS の「出るまでの間」が担う。
    choosing: false, choiceIndex: scene.choice?.defaultIndex ?? 0,
    choiceElapsedMs: 0, choiceResolved: false, resultLines: null, done: false,
  }
  // 先頭が beat/cue の場合に備え、表示すべき最初のテキスト行まで送らない
  //   (テキスト行は入力待ち。 beat/cue は tick で自動処理されるので cursor0 のままでよい)。
  return rt
}

// 選択確定後は resultLines を、それまでは scene.lines を「今読む行の配列」として返す。
function activeLines(rt: AdvRuntime, scene: AdvScene): AdvLine[] {
  return rt.resultLines ?? scene.lines
}

export function isPictureScene(scene: AdvScene): boolean {
  return scene.lines.some(l => l.kind === 'picture')
    || !!scene.choice?.options.some(o => o.resultLines?.some(l => l.kind === 'picture'))
}

// cursor までで最後に hit した行。 選択の結果の文を読んでいる間は、 本文の最後の行を引き継ぐ。
function lastMatching<L extends AdvLine>(rt: AdvRuntime, scene: AdvScene, hit: (line: AdvLine) => line is L): L | null {
  const last = (lines: AdvLine[], cursor: number) => {
    for (let i = Math.min(cursor, lines.length - 1); i >= 0; i--) {
      const line = lines[i]!
      if (hit(line)) return line
    }
    return null
  }
  return last(activeLines(rt, scene), rt.cursor)
    ?? (rt.resultLines ? last(scene.lines, scene.lines.length - 1) : null)
}
function lastLineOf<K extends 'picture' | 'figure'>(rt: AdvRuntime, scene: AdvScene, kind: K): Extract<AdvLine, { kind: K }> | null {
  return lastMatching(rt, scene, (l): l is Extract<AdvLine, { kind: K }> => l.kind === kind)
}

// 鳴らし続ける環境音 (2026-10-27 戴冠獣の広間の空気)。 cue 'amb:<効果音の id>' で鳴らし始め、
//   'amb:off'・'amb:fade' はどちらもゆっくり (400ms) 止める。 鳴らし始めは 1 秒かけて上げる。
//   次の cue まで鳴り続け、 選択の結果の文の間は本文の最後の指示を引き継ぐ。 鳴らすのは main.ts。
//   曲はこの指示に関わらず遠くで鳴らし続ける (2026-10-28 作者「BGMは表現は一旦使わない方針で」)。 空気の音もプツッとは切らない。
export type AdvAmbience = { id: string | null; fadeMs: number }
export function advAmbience(rt: AdvRuntime, scene: AdvScene): AdvAmbience {
  const line = lastMatching(rt, scene, (l): l is Extract<AdvLine, { kind: 'cue' }> => l.kind === 'cue' && l.effect.startsWith('amb:'))
  const what = line?.effect.slice(4) ?? 'off'
  if (what === 'off' || what === 'fade') return { id: null, fadeMs: 400 }
  return { id: what, fadeMs: 1000 }
}

export function currentAdvPicture(rt: AdvRuntime, scene: AdvScene): string | null {
  return lastLineOf(rt, scene, 'picture')?.src ?? null
}

export function currentAdvPictureFadeMs(rt: AdvRuntime, scene: AdvScene): number {
  return lastLineOf(rt, scene, 'picture')?.fadeMs ?? 500
}

// いま立っている人物絵 (無ければ null)。 背景と同じく、 結果の文の間は本文の最後の人物を引き継ぐ。
export function currentAdvFigure(rt: AdvRuntime, scene: AdvScene): AdvFigure | null {
  const line = lastLineOf(rt, scene, 'figure')
  return line?.src ? line : null
}

// いまの気持ちの見せ方 (描画側はこれをそのまま当てる)。 px は画面の座標。
export type AdvStaging = {
  shake: { x: number; y: number }               // 画面全体 (背景・人物・割り込み) のずれ
  figure: { x: number; y: number }              // 人物絵だけのずれ (震え・沈む・跳ねる)
  zoom: { scale: number; x: number; y: number } // 寄り (中心は画面に対する割合)
  flash: number                                 // 白の濃さ 0..1
  insert: AdvInsert | null
  dissolve: AdvDissolve | null
  lamp: AdvLampGlow | null
  wind: AdvWind | null
  collage: AdvCollage | null
  static: AdvStatic | null
}
// 割り込みの1枚。 zoom＝push の寄り・lean＝切り抜き (fig が無ければ全体) の大きさ・under＝重ねて入れ替える前の1枚。
export type AdvInsert = {
  src: string; alpha: number; fig: string | null
  zoom: { scale: number; x: number; y: number }; lean: { scale: number; x: number; y: number }
  under: AdvInsert | null
}
// 光になって消える進み具合。 p=0..1 (1=消えきった)・el=始まってからの ms。 他は AdvStage の dissolve と同じ。
export type AdvDissolve = { p: number; el: number; x: number; y: number; rx: number; ry: number; tint: 'silver' | 'blue'; rise: boolean }
export const ADV_DISSOLVE_MS = 4200
// 常夜燈の漂い。 a＝濃さ 0..1・el＝始まってからの ms (漂いの時計)。
export type AdvLampGlow = { a: number; el: number; x: number; y: number; rx: number; ry: number }
export const ADV_LAMP_GLOW_MS = 2500
// 風で流れる1枚。 p=0..1 (out は 1=消えきった・in は 1=入りきった)。
export type AdvWind = { src: string; dir: 'in' | 'out'; p: number; el: number }
export const ADV_WIND_MS = 2600
// 紙芝居のいまの1枚。 el＝貼られてからの ms (飛ばした時は 1e9＝動き終わった形)。
export type AdvCollage = { page: AdvCollagePage; el: number }
// 砂嵐。 a＝濃さ 0..1・el＝始まってからの ms (ざらつきの時計)。
export type AdvStatic = { a: number; el: number }
export const ADV_STATIC_MS = 1200
const easeOut = (t: number) => 1 - (1 - t) ** 3
export function advStaging(rt: AdvRuntime, scene: AdvScene): AdvStaging {
  const clock = rt.clockMs ?? 0
  const startedAt = new Map<string, number>()
  for (const entry of rt.stageLog ?? []) {
    const [key, at] = entry.split('@'); startedAt.set(key!, Number(at))
  }
  // 通り過ぎた行 (選択後の文の間は、 本文を全部通り過ぎた扱い)。
  const passed: { line: AdvLine; at: number | null }[] = []
  const take = (lines: AdvLine[], until: number, tag: string) => {
    for (let i = 0; i < Math.min(until, lines.length); i++) passed.push({ line: lines[i]!, at: startedAt.get(tag + i) ?? null })
  }
  if (rt.resultLines) { take(scene.lines, scene.lines.length, 'm'); take(rt.resultLines, rt.cursor, 'r') }
  else take(scene.lines, rt.cursor, 'm')

  const out: AdvStaging = { shake: { x: 0, y: 0 }, figure: { x: 0, y: 0 }, zoom: { scale: 1, x: .5, y: .42 }, flash: 0, insert: null, dissolve: null, lamp: null, wind: null, collage: null, static: null }
  let sink = 0
  for (const { line, at } of passed) {
    if (line.kind === 'figure') { sink = 0; continue }
    // 消えきった後に絵を出し直したら、 消えた跡の黒は外す (騎士の Loop＝消えた後に空の甲冑を整える・2026-10-29)。
    // 漂う光と風の1枚は、 その絵の間だけ。
    if (line.kind === 'picture') { out.dissolve = null; out.lamp = null; out.wind = null; out.collage = null; out.static = null; continue }
    if (line.kind !== 'stage') continue
    const el = at === null ? Infinity : clock - at
    const ms = 'ms' in line && line.ms !== undefined ? line.ms : 0
    const t = (dflt: number) => Math.min(1, Math.max(0, el / (ms || dflt)))
    switch (line.effect) {
      case 'zoom': {
        const k = easeOut(t(120)), from = out.zoom
        out.zoom = { scale: from.scale + (line.scale - from.scale) * k, x: from.x + ((line.x ?? .5) - from.x) * k, y: from.y + ((line.y ?? .42) - from.y) * k }
        break
      }
      case 'sink': sink = sink + ((line.power ?? 24) - sink) * easeOut(t(450)); break
      case 'insert': {
        const a = line.fadeMs ? Math.min(1, Math.max(0, el / line.fadeMs)) : 1, prev = out.insert
        if (!line.src) { out.insert = prev && a < 1 ? { ...prev, alpha: prev.alpha * (1 - a), under: null } : null; break }
        const push = line.push, k = push ? Math.min(1, Math.max(0, el / push.ms)) : 0
        out.insert = {
          src: line.src, alpha: a, fig: line.fig ?? null,
          zoom: { scale: push ? 1 + (push.scale - 1) * k : 1, x: push?.x ?? .5, y: push?.y ?? .5 },
          lean: { scale: 1, x: .5, y: .9 },
          under: a < 1 && prev ? { ...prev, under: null } : null,
        }
        break
      }
      case 'lean': {
        if (!out.insert) break
        const u = t(1000)
        out.insert = { ...out.insert, lean: { scale: 1 + ((line.scale ?? 1.04) - 1) * Math.sin(Math.PI * u), x: line.x ?? .5, y: line.y ?? .9 } }
        break
      }
      case 'shake': {
        const k = 1 - t(350); if (k <= 0) break
        const p = (line.power ?? 10) * k
        out.shake.x += p * Math.sin(el * .09); out.shake.y += p * .6 * Math.cos(el * .113)
        break
      }
      case 'tremble': {
        const k = 1 - t(800); if (k <= 0) break
        const p = (line.power ?? 3) * Math.min(1, k * 3)
        out.figure.x += p * (Math.sin(el * .23) + Math.sin(el * .37)) / 2; out.figure.y += p * .4 * Math.sin(el * .31)
        break
      }
      case 'hop': { const u = t(260); if (u < 1) out.figure.y -= (line.power ?? 14) * Math.sin(Math.PI * u); break }
      case 'flash': out.flash = Math.max(out.flash, 1 - t(140)); break
      case 'dissolve':
        out.dissolve = { p: t(ADV_DISSOLVE_MS), el: Number.isFinite(el) ? el : 1e9, x: line.x ?? .5, y: line.y ?? .5, rx: line.rx ?? .3, ry: line.ry ?? .3, tint: line.tint ?? 'silver', rise: line.rise ?? false }
        break
      case 'lampGlow':
        out.lamp = { a: easeOut(t(ADV_LAMP_GLOW_MS)), el: Number.isFinite(el) ? el : 0, x: line.x ?? .5, y: line.y ?? .3, rx: line.rx ?? .36, ry: line.ry ?? .3 }
        break
      case 'static': out.static = { a: t(ADV_STATIC_MS) ** .7, el: Number.isFinite(el) ? el : 1e9 }; break
      case 'collage': out.collage = { page: line.page, el: Number.isFinite(el) ? el : 1e9 }; break
      case 'wind':
        out.wind = { src: line.src, dir: line.dir ?? 'out', p: t(ADV_WIND_MS), el: Number.isFinite(el) ? el : 1e9 }
        break
    }
  }
  out.figure.y += sink
  return out
}

// 最後が「暗転の cue → beat」で終わる場面は、 完走したら送り入力を待たずに次へ抜ける (戦いの前→戦い)。
export function endsInBlackout(scene: AdvScene): boolean {
  const lines = scene.lines
  const cue = lines[lines.length - 2], beat = lines[lines.length - 1]
  return !scene.choice && cue?.kind === 'cue' && cue.effect === 'blackout' && beat?.kind === 'beat'
}

export function advNamePlate(line: AdvTextLine): string | null {
  return line.kind === 'lunaire' ? 'LUNAIRE' : line.kind === 'voice' ? '声'
    : line.kind === 'boss' ? line.name ?? null : null
}

function isTextLine(line: AdvLine): line is AdvTextLine {
  return line.kind === 'voice' || line.kind === 'lunaire' || line.kind === 'other' || line.kind === 'narration' || line.kind === 'boss'
}

// 表示文字列の唯一の出所。描画の区間分割と文字送りはこの同じ文字列を使う。
// 話者名・全角スペース・鉤括弧・丸括弧も、画面に出る1文字としてここに含める。
export type AdvDisplaySegment = { text: string; role: 'speaker' | 'body' }

export function advDisplaySegments(line: AdvTextLine, picture = false): AdvDisplaySegment[] {
  if (picture) {
    const text = line.kind === 'boss' || (line.kind === 'lunaire' && line.speech === 'spoken') ? `「${line.text}」`
      : line.kind === 'lunaire' && line.speech === 'inner' ? `（${line.text}）` : line.text
    return [{ text, role: 'body' }]
  }
  if (line.kind === 'boss') {
    const body: AdvDisplaySegment = { text: `「${line.text}」`, role: 'body' }
    return line.name ? [{ text: `${line.name}　`, role: 'speaker' }, body] : [body]
  }
  if (line.kind === 'lunaire') return [{ text: line.speech === 'spoken' ? `「${line.text}」` : `（${line.text}）`, role: 'body' }]
  return [{ text: line.text, role: 'body' }]
}

export function advDisplayText(line: AdvTextLine, picture = false): string {
  return advDisplaySegments(line, picture).map((segment) => segment.text).join('')
}

export function advDisplayCharacterCount(line: AdvTextLine, picture = false): number {
  return Array.from(advDisplayText(line, picture)).length
}

// Canvas へ渡す前に、コードポイント単位で表示済みの文字だけを残す。
// Array.from を使うので、将来 ADV に絵文字などが入ってもサロゲートペアを途中で切らない。
export function takeAdvDisplayCharacters(text: string, count: number): string {
  return Array.from(text).slice(0, Math.max(0, count)).join('')
}

// 絵だけ見せる間 (文字窓と下の影を描かない)。 即時送りの行を飛ばした先が pictureOnly の beat なら true
//   (beat の手前の寄り・割り込みの行を通る1フレームだけ文字窓が出てちらつかないように)。
export function advPictureOnly(rt: AdvRuntime, scene: AdvScene): boolean {
  if (rt.choosing || rt.done) return false
  const lines = activeLines(rt, scene)
  for (let i = rt.cursor; i < lines.length; i++) {
    const l = lines[i]!
    if (l.kind === 'stage' || l.kind === 'picture' || l.kind === 'figure' || l.kind === 'cue') continue
    return l.kind === 'beat' && !!l.pictureOnly
  }
  return false
}

export function activeTextLine(rt: AdvRuntime, scene: AdvScene): AdvTextLine | null {
  const line = activeLines(rt, scene)[rt.cursor]
  return line && isTextLine(line) ? line : null
}

// 現在見せてよい「テキスト行」の配列 (cursor までの voice/lunaire/other/narration/boss)。 描画用。
export function visibleLines(rt: AdvRuntime, scene: AdvScene): AdvTextLine[] {
  const out: AdvTextLine[] = []
  const lines = activeLines(rt, scene)
  for (let i = 0; i <= rt.cursor && i < lines.length; i += 1) {
    const l = lines[i]!
    if (isTextLine(l)) out.push(l)
  }
  return out
}

// この場面で最終的に出るテキスト行数。 描画側が「先に中央位置を決める」ために使う。
//   visibleLines の数で中央合わせをすると、 1行増えるたびに文章全体がずり上がって
//   読んでいる最中に文字が動いてしまう (2026-07-21)。
export function totalTextLineCount(rt: AdvRuntime, scene: AdvScene): number {
  return activeLines(rt, scene).filter(isTextLine).length
}

function enterChoiceOrDone(rt: AdvRuntime, scene: AdvScene): AdvRuntime {
  if (!rt.choiceResolved && scene.choice) return { ...rt, choosing: true, choiceElapsedMs: 0 }
  return { ...rt, done: true }
}

export function tickAdv(rt: AdvRuntime, scene: AdvScene, input: AdvInput, dtMs: number): AdvTick {
  if (rt.done) return { rt, awakenDelta: 0, firedCue: null }
  return tickAdvLine({ ...rt, clockMs: (rt.clockMs ?? 0) + dtMs }, scene, input, dtMs)
}

function tickAdvLine(rt: AdvRuntime, scene: AdvScene, input: AdvInput, dtMs: number): AdvTick {

  // --- 選択中 ---
  if (rt.choosing && scene.choice) {
    const n = scene.choice.options.length
    // 選択肢が現れきるまでは、 カーソル移動も確定も受け付けない (2026-07-25 第4段-b)。
    //   ここを開ける唯一の条件が isChoiceInputOpen。 描画側の choiceRevealAlpha と対。
    const inputOpen = isChoiceInputOpen(rt)
    let idx = rt.choiceIndex
    if (inputOpen && input.choiceMove !== 0) {
      idx = Math.max(0, Math.min(n - 1, idx + input.choiceMove))
    }
    const elapsed = rt.choiceElapsedMs + dtMs
    const timedOut = scene.choice.timeoutMs != null && elapsed >= scene.choice.timeoutMs
    // タイムアウトの扱いは変えない (時間切れは defaultIndex へ倒れる既存仕様のまま)。
    if ((input.confirm && inputOpen) || timedOut) {
      const chosen = timedOut ? (scene.choice.defaultIndex ?? Math.max(0, idx)) : idx
      const opt = scene.choice.options[chosen]
      const awaken = opt?.awaken ?? 0
      const resultLines = opt?.resultLines && opt.resultLines.length > 0 ? opt.resultLines : null
      if (resultLines) {
        return {
          rt: {
            ...rt, choiceIndex: chosen, choosing: false, choiceResolved: true,
            resultLines, cursor: 0, lineElapsedMs: 0, revealedChars: 0, typewriterElapsedMs: 0,
          },
          awakenDelta: awaken, firedCue: null,
        }
      }
      return { rt: { ...rt, choiceIndex: chosen, choiceResolved: true, done: true }, awakenDelta: awaken, firedCue: null }
    }
    return { rt: { ...rt, choiceIndex: idx, choiceElapsedMs: elapsed }, awakenDelta: 0, firedCue: null }
  }

  const lines = activeLines(rt, scene)
  const line = lines[rt.cursor]
  if (!line) { return { rt: enterChoiceOrDone(rt, scene), awakenDelta: 0, firedCue: null } }

  // --- スキップ: テキスト行を一気に飛ばし、 選択 or 完走で止まる ---
  if (input.skip) {
    // 残りに cue があれば順次発火しないと演出が抜けるので、 1tickでは1cueずつ送る。
    if (line.kind === 'cue') {
      return { rt: advanceCursor(rt, scene), awakenDelta: 0, firedCue: line.effect }
    }
    // cue 以外はまとめて末尾へ
    let c = rt.cursor
    while (c < lines.length && lines[c]!.kind !== 'cue') c += 1
    const moved: AdvRuntime = { ...rt, cursor: c, lineElapsedMs: 0, revealedChars: 0, typewriterElapsedMs: 0 }
    if (c >= lines.length) return { rt: enterChoiceOrDone(moved, scene), awakenDelta: 0, firedCue: null }
    return { rt: moved, awakenDelta: 0, firedCue: null }   // 次tickで cue を処理
  }

  // --- beat: 時間で自動送り (絵だけの間は送り入力でも進む) ---
  if (line.kind === 'beat') {
    const elapsed = rt.lineElapsedMs + dtMs
    if (elapsed >= line.ms || (line.pictureOnly && input.advance)) return { rt: advanceCursor(rt, scene), awakenDelta: 0, firedCue: null }
    return { rt: { ...rt, lineElapsedMs: elapsed }, awakenDelta: 0, firedCue: null }
  }

  // --- cue: 即発火して次へ ---
  if (line.kind === 'cue') {
    return { rt: advanceCursor(rt, scene), awakenDelta: 0, firedCue: line.effect }
  }

  if (line.kind === 'picture' || line.kind === 'figure') return { rt: advanceCursor(rt, scene), awakenDelta: 0, firedCue: null }
  if (line.kind === 'stage') {
    const key = `${rt.resultLines ? 'r' : 'm'}${rt.cursor}@${rt.clockMs ?? 0}`
    return { rt: { ...advanceCursor(rt, scene), stageLog: [...(rt.stageLog ?? []), key] }, awakenDelta: 0, firedCue: null }
  }

  // --- テキスト行: まず1文字ずつ出し、出切った後だけ advance で次へ ---
  const picture = isPictureScene(scene)
  const totalChars = advDisplayCharacterCount(line, picture)
  // pre: 絵が出きるまで文字を出さない。 この間の送り入力は捨てる (絵より先に読み飛ばさない)。
  if (line.pre && rt.lineElapsedMs < line.pre) {
    return { rt: { ...rt, lineElapsedMs: rt.lineElapsedMs + dtMs }, awakenDelta: 0, firedCue: null }
  }
  // auto: 出し切った後の待ちは typewriterElapsedMs で数える (出し切ると 0 に戻るため)。
  if (line.auto != null && rt.revealedChars >= totalChars && !input.advance) {
    const waited = rt.typewriterElapsedMs + dtMs
    if (waited >= line.auto) return { rt: advanceCursor(rt, scene), awakenDelta: 0, firedCue: null }
    return { rt: { ...rt, typewriterElapsedMs: waited }, awakenDelta: 0, firedCue: null }
  }
  if (input.advance) {
    // 1回目は全文を即座に出すだけ。連打で読み飛ばさないよう cursor は動かさない。
    if (rt.revealedChars < totalChars) {
      return { rt: { ...rt, revealedChars: totalChars, typewriterElapsedMs: 0 }, awakenDelta: 0, firedCue: null }
    }
    return { rt: advanceCursor(rt, scene), awakenDelta: 0, firedCue: null }
  }
  return { rt: revealTextCharacters(rt, line, totalChars, dtMs, picture), awakenDelta: 0, firedCue: null }
}

function revealTextCharacters(rt: AdvRuntime, line: AdvTextLine, totalChars: number, dtMs: number, picture: boolean): AdvRuntime {
  let revealedChars = rt.revealedChars
  let elapsed = rt.typewriterElapsedMs + dtMs
  const characters = Array.from(advDisplayText(line, picture))
  const speed = line.pace ? ADV_PACES[line.pace] : ADV_TYPEWRITER
  while (revealedChars < totalChars) {
    const previous = revealedChars > 0 ? characters[revealedChars - 1] : undefined
    // 行末の句読点は、出した瞬間に全文表示完了。次の文字が無いので追加待ちは入れない。
    const extraMs = revealedChars < totalChars && previous === '。'
      ? speed.fullStopExtraMs
      : previous === '、' ? speed.commaExtraMs : 0
    const needed = speed.charMs + extraMs
    if (elapsed < needed) break
    elapsed -= needed
    revealedChars += 1
  }
  // 出切った行は次の行に持ち越す待ち時間を持たない。
  return { ...rt, revealedChars, typewriterElapsedMs: revealedChars === totalChars ? 0 : elapsed }
}

// cursor を1つ進める。 末尾を越えたら choice or done に遷移。
function advanceCursor(rt: AdvRuntime, scene: AdvScene): AdvRuntime {
  const lines = activeLines(rt, scene)
  const next = rt.cursor + 1
  if (next >= lines.length) return enterChoiceOrDone({ ...rt, cursor: lines.length, lineElapsedMs: 0, revealedChars: 0, typewriterElapsedMs: 0 }, scene)
  return { ...rt, cursor: next, lineElapsedMs: 0, revealedChars: 0, typewriterElapsedMs: 0 }
}

// cue の演出尺は「cue行の直後の beat行」が持つ (cue単体は発火即進行で持続しないため)。
//   いまアクティブな行が beat で、その1つ前が cue なら、その cue と beatの進捗(0..1)を返す。
//   描画側 (render.ts) がフラッシュバック/終幕FXをステートレスに描くための純ヘルパ。
export function activeCueBeat(rt: AdvRuntime, scene: AdvScene): { effect: AdvCue; progress: number } | null {
  const lines = rt.resultLines ?? scene.lines
  const cur = lines[rt.cursor]
  if (!cur || cur.kind !== 'beat') return null
  const prev = lines[rt.cursor - 1]
  if (!prev || prev.kind !== 'cue') return null
  const progress = cur.ms > 0 ? Math.max(0, Math.min(1, rt.lineElapsedMs / cur.ms)) : 1
  return { effect: prev.effect, progress }
}
